import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const GeoContext = createContext();

const USD_BRL_RATE = 5.45;
const roundUpToTenCents = (value) => Math.ceil(value * 10) / 10;

const INTL_LINKS = {
  '1-15dias': 'https://go.centerpag.com/PPU38CQ9HQ8',
  '1-mensal': 'https://go.centerpag.com/PPU38CQ9HQ8',
  '1-trimestral': 'https://go.centerpag.com/PPU38CQ9IC1',
  '1-permanente': 'https://go.centerpag.com/PPU38CQ9IC3',
  '2-15dias': 'https://go.centerpag.com/PPU38CQ9ICK',
  '2-mensal': 'https://go.centerpag.com/PPU38CQ9ICK',
  '2-trimestral': 'https://go.centerpag.com/PPU38CQ9ICL',
  '2-permanente': 'https://go.centerpag.com/PPU38CQ9ICU',
  '3-15dias': 'https://go.centerpag.com/PPU38CQ9ICK',
  '3-mensal': 'https://go.centerpag.com/PPU38CQFJN1',
  '3-trimestral': 'https://go.centerpag.com/PPU38CQFJN2',
  '3-permanente': 'https://go.centerpag.com/PPU38CQFJN3',
  '5-15dias': 'https://go.centerpag.com/PPU38CQ9HQ8',
  '5-mensal': 'https://go.centerpag.com/PPU38CQ9HQ8',
  '5-permanente': 'https://go.centerpag.com/PPU38CQ9IC3',
};

export const GeoProvider = ({ children }) => {
  const [isBrazil, setIsBrazil] = useState(true);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('BR');

  useEffect(() => {
    const fetchWithTimeout = async (url, ms = 4000) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), ms);
      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
      }
    };

    // Multiplos provedores para garantir deteccao mesmo se um falhar/for bloqueado
    const providers = [
      async () => (await fetchWithTimeout('https://ipapi.co/json/'))?.country_code,
      async () => (await fetchWithTimeout('https://ipwho.is/'))?.country_code,
      async () => (await fetchWithTimeout('https://api.country.is/'))?.country,
      async () => (await fetchWithTimeout('https://get.geojs.io/v1/ip/country.json'))?.country,
    ];

    const detectLocation = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const override = params.get('country');
        if (override) {
          const code = override.toUpperCase();
          setCountry(code);
          setIsBrazil(code === 'BR');
          setLoading(false);
          return;
        }

        for (const getCode of providers) {
          const code = await getCode();
          if (code && typeof code === 'string' && code.length === 2) {
            const upper = code.toUpperCase();
            setCountry(upper);
            setIsBrazil(upper === 'BR');
            setLoading(false);
            return;
          }
        }
        // Nenhum provedor respondeu -> fallback BR
        setIsBrazil(true);
        setCountry('BR');
      } catch {
        setIsBrazil(true);
        setCountry('BR');
      } finally {
        setLoading(false);
      }
    };
    detectLocation();
  }, []);

  const currency = isBrazil ? 'R$' : '$';

  const getPrice = useCallback((planId, brlPrice) => {
    if (isBrazil) return brlPrice;
    return roundUpToTenCents(brlPrice / USD_BRL_RATE);
  }, [isBrazil]);

  const getOriginalPrice = useCallback((planId, brlOriginal) => {
    if (isBrazil) return brlOriginal;
    return roundUpToTenCents(brlOriginal / USD_BRL_RATE);
  }, [isBrazil]);

  const getIntlLink = useCallback((planId) => {
    return INTL_LINKS[planId] || null;
  }, []);

  // Moeda por plano: internacional sempre em USD (conversao direta da cotacao)
  const getPlanCurrency = useCallback((planId) => {
    return isBrazil ? 'R$' : '$';
  }, [isBrazil]);

  const value = useMemo(() => ({
    isBrazil, loading, country, currency, getPrice, getOriginalPrice, getIntlLink, getPlanCurrency
  }), [isBrazil, loading, country, currency, getPrice, getOriginalPrice, getIntlLink, getPlanCurrency]);

  return (
    <GeoContext.Provider value={value}>
      {children}
    </GeoContext.Provider>
  );
};

export const useGeo = () => useContext(GeoContext);
